import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, CardBody, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { toast } from "react-toastify";
import _ from 'lodash';
import { ServiceThreadSpinner } from '.';
import { DesktopContainer } from '../layouts';
import { api, dateHelpers, helpers, UserContext } from '../utils';

// Admin Tools > Automation Health. One call (Health/Automations) returns the rows AND the severity
// counts, so the summary cards can never disagree with the list. A second, click-only call
// (Health) runs the live portal checks.

const REFRESH_INTERVAL_MS = 60000;
const DATE_FORMAT = "MM-DD-YYYY HH:mm";
const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 1440;

const SEVERITY = {
   Red: "Red",
   Amber: "Amber",
   Green: "Green",
   Idle: "Idle",
};

// Display order of the cards; label is the text that always accompanies the color.
const SEVERITY_INFO = [
   {
      key: SEVERITY.Red, countField: "red", label: "Needs attention", cssClass: "red",
      explanation: "This automation failed, or is well past its expected window.",
   },
   {
      key: SEVERITY.Amber, countField: "amber", label: "Overdue", cssClass: "amber",
      explanation: "This automation has not checked in within its expected window.",
   },
   {
      key: SEVERITY.Green, countField: "green", label: "Healthy", cssClass: "green",
      explanation: "This automation ran successfully within its expected window.",
   },
   {
      key: SEVERITY.Idle, countField: "idle", label: "Not reporting", cssClass: "idle",
      explanation: "Switched off, never run, or no result yet.",
   },
];

// Shown in place of a switched-off row's stored Detail, which names the internal setting (kept in the table for whoever
// checks the database or logs; not shown on the screen - Carolyn 2026-10-23).
const DISABLED_DETAIL_TEXT = "Switched off in this site's settings.";

// A gray (Idle) row says which kind of gray it is; the card above counts all three together.
const IDLE_KIND = {
   disabled: { label: "Disabled", explanation: "This automation is switched off." },
   neverRun: { label: "Never run", explanation: "This automation has not reported a run here yet." },
   noResult: { label: "No result", explanation: "This automation reported without an OK or failed result." },
};

const RUN_STATUS = {
   ok: { label: "OK", cssClass: "green" },
   fail: { label: "FAILED", cssClass: "red" },
   unknown: { label: "NO DATA", cssClass: "idle" },
};

const EMPTY_RESULT = { automations: [], red: 0, amber: 0, green: 0, idle: 0, asOf: null };
const LOAD_FAILED_MESSAGE = "Unable to load automation health.";
const LOAD_FAILED_EMPTY_MESSAGE = "Automation health could not be loaded. It retries every minute.";
const LOAD_FAILED_TOAST_ID = "automation-health-load";   // one toast, not one per 60 s refresh
const CHECKS_FAILED_MESSAGE = "Unable to run the portal checks.";
const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;

// The shared axios interceptor RESOLVES on an HTTP error (it returns the error object), so a failed
// call arrives in .then with no response.data - never in .catch. Returns true when the call failed,
// after toasting it. A 401 or 403 is already handled and toasted by the interceptor.
function reportFailure(response, fallbackMessage, toastId) {
   const body = response?.data;
   if (body && body.success !== false) return false;
   const status = response?.response?.status;
   if (status !== HTTP_UNAUTHORIZED && status !== HTTP_FORBIDDEN) {
      toast.error(body?.message || fallbackMessage, toastId ? { toastId } : undefined);
   }
   return true;
}

function infoFor(severity) {
   return _.find(SEVERITY_INFO, (s) => s.key === severity) || SEVERITY_INFO[SEVERITY_INFO.length - 1];
}

// The label and explanation for one row: the severity's own, except that a gray row names its kind (isDisabled is set
// by the server from the switched-off Detail; no run time = never run; anything else gray = no result).
function rowInfo(automation) {
   const info = infoFor(automation.severity);
   if (info.key !== SEVERITY.Idle) return info;
   if (automation.isDisabled) return { ...info, ...IDLE_KIND.disabled };
   if (automation.ageMinutes === null || automation.ageMinutes === undefined) return { ...info, ...IDLE_KIND.neverRun };
   return { ...info, ...IDLE_KIND.noResult };
}

function formatDate(value) {
   return value ? dateHelpers.dateTimeFormat(value, DATE_FORMAT) : "-";
}

// The health monitor's own last check, shown beside "as of"; a missing or overdue monitor says so (styled red by the caller).
function monitorCheckedText(board) {
   if (!board.monitorLastCheckedAt) return "Monitor has not reported";
   const text = `Monitor last checked ${formatDate(board.monitorLastCheckedAt)}`;
   return board.monitorStale ? `${text} - overdue` : text;
}

// "last run 5 min ago", "3 h ago", "2 d ago". No time = never run, which the row's badge already says, so "-" here. A
// switched-off worker writes its row without running, so its time is when it last checked (isDisabled, set by the server).
function formatAge(ageMinutes, isDisabled) {
   if (ageMinutes === null || ageMinutes === undefined) return "-";
   const verb = isDisabled ? "checked" : "last run";
   if (ageMinutes < MINUTES_PER_HOUR) return `${verb} ${Math.round(ageMinutes)} min ago`;
   if (ageMinutes < MINUTES_PER_DAY) return `${verb} ${Math.round(ageMinutes / MINUTES_PER_HOUR)} h ago`;
   return `${verb} ${Math.round(ageMinutes / MINUTES_PER_DAY)} d ago`;
}


export default function AutomationHealth() {
   const currentUser = helpers.getCurrentUser();
   const noRoles = helpers.hasNoRoles(currentUser);

   const [result, setResult] = useState(EMPTY_RESULT);
   const [loaded, setLoaded] = useState(false);
   const [loadFailed, setLoadFailed] = useState(false);
   const [filter, setFilter] = useState(null);
   const [selected, setSelected] = useState(null);

   // Only the newest request may update the board - a visibility refresh can overlap the interval's.
   const latestRequestId = useRef(0);

   const loadAutomations = useCallback(() => {
      const requestId = ++latestRequestId.current;
      api
         .fetch("Health/Automations")
         .then((response) => {
            if (requestId !== latestRequestId.current) return;
            // A failed call must never look like an empty, healthy board - keep the last good board on screen.
            if (reportFailure(response, LOAD_FAILED_MESSAGE, LOAD_FAILED_TOAST_ID)) {
               setLoadFailed(true);
               return;
            }
            const body = response.data;
            setResult({ ...EMPTY_RESULT, ...body, automations: body.automations || [] });
            setLoadFailed(false);
         })
         .catch((error) => {
            if (requestId !== latestRequestId.current) return;
            setLoadFailed(true);
            api.catchHandler(error);
            toast.error(LOAD_FAILED_MESSAGE, { toastId: LOAD_FAILED_TOAST_ID });
         })
         .finally(() => {
            if (requestId !== latestRequestId.current) return;
            setLoaded(true);
         });
   }, []);

   useEffect(() => {
      document.title = `Automation Health`;
   }, []);

   // Load on mount, then refresh every minute while the tab is visible and again the moment it
   // becomes visible. An account with no roles makes no API call at all.
   useEffect(() => {
      if (noRoles) return undefined;
      loadAutomations();
      const timer = setInterval(() => {
         if (document.visibilityState === "visible") loadAutomations();
      }, REFRESH_INTERVAL_MS);
      const onVisibilityChange = () => {
         if (document.visibilityState === "visible") loadAutomations();
      };
      document.addEventListener("visibilitychange", onVisibilityChange);
      return () => {
         clearInterval(timer);
         document.removeEventListener("visibilitychange", onVisibilityChange);
      };
   }, [noRoles, loadAutomations]);

   const groups = useMemo(() => {
      const shown = filter ? _.filter(result.automations, (a) => a.severity === filter) : result.automations;
      return _.map(_.groupBy(shown, "groupName"), (rows, groupName) => ({ groupName, rows }));
   }, [result.automations, filter]);

   // A failed load with nothing on screen must not read as "nothing is reporting".
   let emptyMessage = "No automations are reporting.";
   if (loadFailed && result.automations.length === 0) emptyMessage = LOAD_FAILED_EMPTY_MESSAGE;
   else if (filter) emptyMessage = "No automations in this state.";

   // Same card again clears the filter.
   function onCardClick(key) {
      setFilter((prior) => (prior === key ? null : key));
   }

   return (
      <DesktopContainer screenName="Automation Health">
         <div className="automation-health-page">
            <Card className="dashboard-card">
               <CardBody>
                  <div className="d-flex flex-wrap align-items-center justify-content-between">
                     <span className="h2 me-2">Automation Health</span>
                     <span className="automation-health-secondary">
                        {result.asOf ? `as of ${formatDate(result.asOf)}` : ""}
                        {result.asOf ? (
                           <span className={result.monitorStale ? "ms-3 overdue" : "ms-3"}>
                              {monitorCheckedText(result)}
                           </span>
                        ) : null}
                     </span>
                  </div>
                  <hr />
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                     {_.map(SEVERITY_INFO, (info) => (
                        <SummaryCard
                           key={info.key}
                           info={info}
                           count={result[info.countField] ?? 0}
                           active={filter === info.key}
                           onClick={onCardClick}
                        />
                     ))}
                     {filter ? (
                        <Button size="sm" color="link" onClick={() => setFilter(null)}>Show all</Button>
                     ) : null}
                  </div>
                  <ServiceThreadSpinner on={!loaded && !noRoles}>
                     {groups.length === 0 && loaded ? (
                        <div className="text-center my-3">{emptyMessage}</div>
                     ) : null}
                     {_.map(groups, (group) => (
                        <AutomationGroup key={group.groupName} group={group} onSelect={setSelected} />
                     ))}
                  </ServiceThreadSpinner>
               </CardBody>
            </Card>

            <PortalChecks />
         </div>
         <DetailModal automation={selected} onClose={() => setSelected(null)} />
      </DesktopContainer>
   );
}


function SummaryCard({ info, count, active, onClick }) {
   return (
      <button
         type="button"
         className={`automation-health-summary ${info.cssClass}${active ? " active" : ""}`}
         aria-pressed={active}
         onClick={() => onClick(info.key)}
      >
         <span className="automation-health-summary-count">{count}</span>
         <span className="automation-health-summary-label">{info.label}</span>
      </button>
   );
}


function SeverityBadge({ automation }) {
   const info = rowInfo(automation);
   return (
      <span className="automation-health-severity">
         <span className={`automation-health-dot ${info.cssClass}`} />
         {info.label}
      </span>
   );
}


function RunStatusBadge({ runStatus }) {
   const status = RUN_STATUS[runStatus] || RUN_STATUS.unknown;
   return <span className={`automation-health-status ${status.cssClass}`}>{status.label}</span>;
}


function AutomationGroup({ group, onSelect }) {
   return (
      <div className="automation-health-group">
         <div className="automation-health-group-title">{group.groupName}</div>
         {_.map(group.rows, (row) => (
            <AutomationRow key={row.automationKey} row={row} onSelect={onSelect} />
         ))}
      </div>
   );
}


function AutomationRow({ row, onSelect }) {
   return (
      <div
         className="automation-health-row"
         role="button"
         tabIndex={0}
         onClick={() => onSelect(row)}
         onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
               e.preventDefault();
               onSelect(row);
            }
         }}
      >
         <div className="automation-health-cell severity"><SeverityBadge automation={row} /></div>
         <div className="automation-health-cell name">{row.displayName}</div>
         <div className="automation-health-cell age">{formatAge(row.ageMinutes, row.isDisabled)}</div>
         <div className="automation-health-cell status"><RunStatusBadge runStatus={row.runStatus} /></div>
         <div className="automation-health-cell host">{row.sourceHost}</div>
      </div>
   );
}


function DetailLine({ label, value }) {
   return (
      <Row className="mb-2">
         <Col xs={4} className="fw-bold">{label}</Col>
         <Col xs={8}>{value === null || value === undefined || value === "" ? "-" : value}</Col>
      </Row>
   );
}


function DetailModal({ automation, onClose }) {
   // A modal renders outside the themed page, so it carries the theme itself (as ConfirmModal does).
   const userCtx = useContext(UserContext);
   const modalClassName = userCtx.theme ? `${userCtx.theme}` : "lightTheme";
   // Keep the last automation rendered while the modal fades out.
   const lastShown = useRef(null);
   if (automation) lastShown.current = automation;
   const a = lastShown.current;
   return (
      <Modal isOpen={!!automation} toggle={onClose} centered contentClassName={modalClassName}>
         <ModalHeader toggle={onClose}>{a?.displayName}</ModalHeader>
         {a ? (
            <ModalBody>
               <p><SeverityBadge automation={a} /> - {rowInfo(a).explanation}</p>
               {a.isDisabled ? (
                  <DetailLine label="Last checked" value={formatDate(a.lastRunAt)} />
               ) : (
                  <>
                     <DetailLine label="Last run started" value={formatDate(a.lastRunAt)} />
                     <DetailLine label="Last run finished" value={formatDate(a.lastFinishAt)} />
                  </>
               )}
               <DetailLine label="Run status" value={(RUN_STATUS[a.runStatus] || RUN_STATUS.unknown).label} />
               <DetailLine label="Rows affected" value={a.rowsAffected} />
               <DetailLine label="Detail" value={a.isDisabled ? DISABLED_DETAIL_TEXT : a.detail} />
               <DetailLine label="Source host" value={a.sourceHost} />
               <DetailLine
                  label="Expected"
                  value={a.staleAfterMinutes == null ? null : `at least every ${a.staleAfterMinutes} min`}
               />
            </ModalBody>
         ) : null}
         <ModalFooter>
            <Button color="secondary" onClick={onClose}>Close</Button>
         </ModalFooter>
      </Modal>
   );
}


// Click-only live checks (database, GP, Duo, ...). Duo can take ~15 s, hence the spinner.
function PortalChecks() {
   const [checks, setChecks] = useState(null);
   const [running, setRunning] = useState(false);
   const runningRef = useRef(false);

   function runChecks() {
      if (runningRef.current) return;
      runningRef.current = true;
      setRunning(true);
      api
         .fetch("Health")
         .then((response) => {
            if (reportFailure(response, CHECKS_FAILED_MESSAGE)) return;
            setChecks(response.data);
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(CHECKS_FAILED_MESSAGE);
         })
         .finally(() => {
            runningRef.current = false;
            setRunning(false);
         });
   }

   return (
      <Card className="dashboard-card">
         <CardBody>
            <div className="d-flex flex-wrap align-items-center justify-content-between">
               <span className="h2 me-2">Portal checks</span>
               <Button color="primary" disabled={running} onClick={runChecks}>Run checks</Button>
            </div>
            <hr />
            <ServiceThreadSpinner on={running}>
               {checks ? (
                  <>
                     {_.map(checks.components || [], (c) => (
                        <div className="automation-health-row static" key={c.name}>
                           <div className="automation-health-cell name">{c.name}</div>
                           <div className="automation-health-cell status">
                              <span className={`automation-health-status ${c.ok ? "green" : "red"}`}>
                                 {c.ok ? "OK" : "FAILED"}
                              </span>
                           </div>
                           <div className="automation-health-cell age">{c.elapsedMs ?? 0} ms</div>
                           <div className="automation-health-cell host">{c.detail}</div>
                        </div>
                     ))}
                     <div className="automation-health-secondary mt-2">
                        Version {checks.appVersion} on {checks.machineName}, started {formatDate(checks.processStartedAt)}
                     </div>
                  </>
               ) : (
                  <div className="automation-health-secondary">
                     Run checks to test the database, integrations and sign-in services right now.
                  </div>
               )}
            </ServiceThreadSpinner>
         </CardBody>
      </Card>
   );
}
